// Post Type Selector Web Component
// Accessible dropdown for selecting post types
import { createPopupMenuPortal } from '../utils/popup-menu-portal.js';

class PostTypeSelector extends HTMLElement {
  constructor() {
    super();
    this.selectedType = 'text';
    this.isDropdownOpen = false;
    this.focusedIndex = -1;
    this._popupPortal = null;
    this.postTypes = [
      { type: 'text', icon: '📝', label: 'Write Post' },
      { type: 'mood', icon: '🌈', label: 'Share Mood' },
      { type: 'music', icon: '🎵', label: 'Share Music' },
      { type: 'poll', icon: '📊', label: 'Poll' }
    ];
  }

  static get observedAttributes() {
    return ['selected-type'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-type' && newValue !== oldValue) {
      this.selectedType = newValue || 'text';
      this.updateDisplay();
    }
  }

  connectedCallback() {
    const selectedTypeAttr = this.getAttribute('selected-type');
    if (selectedTypeAttr) {
      this.selectedType = selectedTypeAttr;
    }
    this.render();
    this.attachEventListeners();
  }

  getCurrentPostType() {
    return this.postTypes.find(pt => pt.type === this.selectedType) || this.postTypes[0];
  }

  render() {
    const currentType = this.getCurrentPostType();
    const menuId = `post-type-menu-${this.generateId()}`;
    const buttonId = `post-type-button-${this.generateId()}`;

    this.innerHTML = `
      <button 
        type="button" 
        id="${buttonId}"
        class="post-type-dropdown-btn active" 
        aria-haspopup="listbox"
        aria-expanded="${this.isDropdownOpen}"
        aria-controls="${menuId}"
        aria-label="Select post type. Current: ${currentType?.label || 'Write Post'}"
      >
        <span class="post-type-label">
          ${currentType?.icon || '📝'} ${currentType?.label || 'Write Post'}
        </span>
        <span class="dropdown-arrow" aria-hidden="true">▼</span>
      </button>
      <ul 
        id="${menuId}"
        class="post-type-menu ${this.isDropdownOpen ? 'open' : ''}"
        role="listbox"
        aria-label="Post type options"
      >
        ${this.postTypes.map((pt, index) => `
          <li 
            role="option"
            class="post-type-menu-item ${this.selectedType === pt.type ? 'selected' : ''} ${index === this.focusedIndex ? 'focused' : ''}"
            data-type="${pt.type}"
            data-index="${index}"
            aria-selected="${this.selectedType === pt.type}"
            tabindex="-1"
          >
            ${pt.icon} ${pt.label}
          </li>
        `).join('')}
      </ul>
    `;
  }

  generateId() {
    return Math.random().toString(36).substring(2, 9);
  }

  attachEventListeners() {
    const dropdownButton = this.querySelector('.post-type-dropdown-btn');
    const menu = this.querySelector('.post-type-menu');
    const menuItems = this.getMenuItems();

    if (!dropdownButton || !menu) return;

    if (!this._popupPortal) {
      this._popupPortal = createPopupMenuPortal(this, {
        getMenuContainer: () => this,
        menuSelector: '.post-type-menu',
        openClass: 'open',
        align: 'left',
        gap: 8,
        onClose: () => {
          this.isDropdownOpen = false;
          this.focusedIndex = -1;
          this.updateDropdownState();
        }
      });
    }

    // Dropdown button click
    dropdownButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleDropdown();
    });

    // Keyboard navigation on button
    dropdownButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.openDropdown();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.openDropdown();
        this.focusMenuItem(0);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.openDropdown();
        this.focusMenuItem(menuItems.length - 1);
      }
    });

    // Menu item click
    menuItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        const type = item.dataset.type;
        this.selectPostType(type);
      });

      // Keyboard navigation on menu items
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const type = item.dataset.type;
          this.selectPostType(type);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex = (index + 1) % menuItems.length;
          this.focusMenuItem(nextIndex);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex = (index - 1 + menuItems.length) % menuItems.length;
          this.focusMenuItem(prevIndex);
        } else if (e.key === 'Home') {
          e.preventDefault();
          this.focusMenuItem(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          this.focusMenuItem(menuItems.length - 1);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.closeDropdown();
          dropdownButton?.focus();
        }
      });
    });

    // Close dropdown on Escape key (outside click is handled by portal)
    const handleEscape = (e) => {
      if (e.key === 'Escape' && this.isDropdownOpen) {
        this.closeDropdown();
        dropdownButton?.focus();
      }
    };

    this._escapeHandler = handleEscape;
    document.addEventListener('keydown', handleEscape);
  }

  disconnectedCallback() {
    this._popupPortal?.close();
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
    }
  }

  toggleDropdown() {
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    const dropdownButton = this.querySelector('.post-type-dropdown-btn');
    const menu = this.querySelector('.post-type-menu');
    if (!dropdownButton || !menu || !this._popupPortal) return;
    this.isDropdownOpen = true;
    this.focusedIndex = -1;
    this._popupPortal.open(dropdownButton, menu);
    this.updateDropdownState();
  }

  closeDropdown() {
    this._popupPortal?.close();
  }

  /** Menu may be in host or portaled to body; use portal.getMenu() when open. */
  getMenu() {
    return this._popupPortal?.getMenu() ?? this.querySelector('.post-type-menu');
  }

  getMenuItems() {
    const menu = this.getMenu();
    return menu ? Array.from(menu.querySelectorAll('.post-type-menu-item')) : [];
  }

  updateDropdownState() {
    const button = this.querySelector('.post-type-dropdown-btn');
    const menu = this.getMenu();
    const currentType = this.getCurrentPostType();

    if (button) {
      button.setAttribute('aria-expanded', this.isDropdownOpen);
      button.setAttribute('aria-label', `Select post type. Current: ${currentType?.label || 'Write Post'}`);
    }

    if (menu) {
      menu.classList.toggle('open', this.isDropdownOpen);
    }

    const menuItems = this.getMenuItems();
    menuItems.forEach((item, index) => {
      item.classList.toggle('focused', index === this.focusedIndex);
    });
  }

  focusMenuItem(index) {
    const menuItems = this.getMenuItems();
    if (index >= 0 && index < menuItems.length) {
      this.focusedIndex = index;
      menuItems[index].focus({ preventScroll: true });
      this.updateDropdownState();
    }
  }

  selectPostType(type) {
    this.selectedType = type;
    this.setAttribute('selected-type', type);
    this.closeDropdown();
    this.updateDisplay();

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('post-type-selected', {
      detail: { type },
      bubbles: true
    }));

    // Return focus to button
    const button = this.querySelector('.post-type-dropdown-btn');
    button?.focus();
  }

  updateDisplay() {
    const currentType = this.getCurrentPostType();
    const button = this.querySelector('.post-type-dropdown-btn');
    const label = this.querySelector('.post-type-label');

    if (button) {
      button.setAttribute('aria-label', `Select post type. Current: ${currentType?.label || 'Write Post'}`);
    }

    if (label) {
      label.innerHTML = `${currentType?.icon || '📝'} ${currentType?.label || 'Write Post'}`;
    }

    // Update selected state in menu
    const menuItems = this.getMenuItems();
    menuItems.forEach(item => {
      const isSelected = item.dataset.type === this.selectedType;
      item.classList.toggle('selected', isSelected);
      item.setAttribute('aria-selected', isSelected);
    });
  }

  setSelectedType(type) {
    this.selectedType = type;
    this.setAttribute('selected-type', type);
    this.updateDisplay();
  }
}

// Register the custom element
customElements.define('post-type-selector', PostTypeSelector);
